package fr.manyverse.app.presentation.components.premade

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.focusable
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.PagerState
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.dp
import fr.manyverse.app.domain.model.PreMadeCharacter
import kotlinx.coroutines.launch
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.premade_go_to_page
import org.jetbrains.compose.resources.stringResource

/**
 * Pager infini virtuel pour parcourir les pré-tirés, modèle repris du dialog de choix côté joueur.
 * La page initiale est placée au centre afin d'autoriser le défilement dans les deux sens.
 */
@Composable
fun rememberPreMadeCarouselState(itemCount: Int): PagerState {
    val virtualPageCount = if (itemCount <= 0) 1 else itemCount * 10_000
    val initialPage = if (itemCount <= 0) 0 else virtualPageCount / 2
    return rememberPagerState(initialPage = initialPage, pageCount = { virtualPageCount })
}

/**
 * Carrousel partagé d'affichage des pré-tirés : [HorizontalPager] infini + navigation clavier
 * (gauche : Left/A/Q · droite : Right/D) + rangée de points indicateurs.
 *
 * Le [pagerState] est hoissé (créé via [rememberPreMadeCarouselState]) pour que l'appelant puisse
 * lire la page courante (`pagerState.currentPage % items.size`) — utile au dialog de choix pour ses
 * boutons Choisir / Passer. Le contenu de chaque page est délégué à [page].
 */
@Composable
fun PreMadeCarousel(
    items: List<PreMadeCharacter>,
    pagerState: PagerState,
    modifier: Modifier = Modifier,
    page: @Composable (PreMadeCharacter) -> Unit
) {
    if (items.isEmpty()) return

    val coroutineScope = rememberCoroutineScope()
    val focusRequester = remember { FocusRequester() }

    LaunchedEffect(Unit) {
        focusRequester.requestFocus()
    }

    Column(
        modifier = modifier.pointerInput(Unit) {
            detectTapGestures(onTap = { focusRequester.requestFocus() })
        },
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        HorizontalPager(
            state = pagerState,
            modifier = Modifier
                .weight(1f)
                .fillMaxWidth()
                .focusRequester(focusRequester)
                .focusable()
                .onKeyEvent { event ->
                    if (event.type == KeyEventType.KeyDown) {
                        when (event.key) {
                            Key.DirectionLeft, Key.A, Key.Q -> {
                                coroutineScope.launch {
                                    pagerState.animateScrollToPage(pagerState.currentPage - 1)
                                }
                                true
                            }

                            Key.DirectionRight, Key.D -> {
                                coroutineScope.launch {
                                    pagerState.animateScrollToPage(pagerState.currentPage + 1)
                                }
                                true
                            }

                            else -> false
                        }
                    } else false
                }
        ) { virtualPage ->
            page(items[virtualPage % items.size])
        }

        if (items.size > 1) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(vertical = 4.dp),
                horizontalArrangement = Arrangement.Center
            ) {
                repeat(items.size) { index ->
                    val color = if (pagerState.currentPage % items.size == index) {
                        MaterialTheme.colorScheme.primary
                    } else {
                        MaterialTheme.colorScheme.surfaceVariant
                    }
                    val description = stringResource(Res.string.premade_go_to_page, index + 1)
                    Box(
                        modifier = Modifier
                            .size(24.dp)
                            .clickable {
                                val currentIndex = pagerState.currentPage % items.size
                                coroutineScope.launch {
                                    pagerState.animateScrollToPage(pagerState.currentPage + index - currentIndex)
                                }
                                focusRequester.requestFocus()
                            }
                            .semantics { contentDescription = description },
                        contentAlignment = Alignment.Center
                    ) {
                        Box(
                            modifier = Modifier
                                .size(8.dp)
                                .background(color, CircleShape)
                        )
                    }
                }
            }
        }
    }
}
